/**
 * Data transformer for Active Projects API response
 * Normalizes API data structure to match datatable column expectations
 */

export interface TransformedActiveProject {
  id: string;
  title: string;
  description?: string;
  addedOn: string;
  productSpecificationType: string;
  productType: string;
  sku: string;
  giftPack: string;
  amountOfProductsInGiftPack: string;
  retailPrice: number;
  businessCategory: {
    id: string;
    categoryId: string;
    description: string;
    categoryType: string;
    leader: string;
    manager: string | null;
  };
  categoryManagerUser: {
    id: string;
    firstname: string;
    username: string;
    email: string;
  };
  qa: {
    id: string;
    firstname: string;
    username: string;
    email: string;
  };
  productCreator: {
    id: string;
    firstname: string;
    username: string;
    email: string;
  };
  nutrition: {
    id: string;
    firstname: string;
    username: string;
    email: string;
  };
  buyer: {
    id: string;
    firstname: string;
    username: string;
    email: string;
  };
  variants: Array<{
    variantId: number;
    variantName: string;
    sku: string;
    retailPrice: number;
    packSize: string;
    isDefault: boolean;
  }>;
  vendorAssessments: Array<{
    id: string;
    status: string;
    vendor: {
      id: string;
      vendorCorporateName: string;
    };
    vendorManufacturingFacilities: Array<{
      id: string;
      name: string;
      address: string;
      city: string;
      state: string;
      zip: string;
      country: string;
    }>;
  }>;
  // Also create vendorDetails for MultiVendorTooltip compatibility
  vendorDetails: Array<{
    id: string;
    vendorName: string;
    vendorAssessmentStatus: string;
    published: boolean | null;
    artworkDeadline: string;
    workflowStatus: string[];
  }>;
  // Additional fields for datatable compatibility
  businessStatus?: string;
  status?: string; // Top-level status field
  vendor?: string | string[]; // Flattened vendor names
  category?: string; // Alias for productSpecificationType
}

/**
 * Create a fallback item for invalid data
 * @param index - Index of the invalid item
 * @returns Fallback TransformedActiveProject
 */
function createFallbackItem(index: number): TransformedActiveProject {
  return {
    id: `error-${index}`,
    title: 'Error Loading Project',
    description: '',
    addedOn: new Date().toISOString(),
    productSpecificationType: 'Unknown',
    sku: '',
    productType: '',
    giftPack: 'false',
    amountOfProductsInGiftPack: '1',
    retailPrice: 0,
    businessCategory: { id: '', categoryId: '', description: '', categoryType: '', leader: '', manager: null },
    categoryManagerUser: { id: '', firstname: '', username: '', email: '' },
    qa: { id: '', firstname: '', username: '', email: '' },
    productCreator: { id: '', firstname: '', username: '', email: '' },
    nutrition: { id: '', firstname: '', username: '', email: '' },
    buyer: { id: '', firstname: '', username: '', email: '' },
    variants: [],
    vendorAssessments: [],
    vendorDetails: [],
    businessStatus: '',
    status: 'Error',
    vendor: 'Unknown',
    category: 'Unknown'
  };
}

/**
 * Transform raw API data to normalized structure
 * @param rawData - Raw API response data
 * @param isVendor - Flag indicating if the current user is a vendor
 * @returns Transformed data array
 */
export function transformActiveProjectsData(rawData: any[], isVendor: boolean = false): TransformedActiveProject[] {
  if (!Array.isArray(rawData)) {
    return [];
  }

  return rawData.map((item, index) => {
    try {
      // Comprehensive null checks for item
      if (!item || typeof item !== 'object') {
        return createFallbackItem(index);
      }

      // Extract vendor information from vendorDetails (API structure)
      const vendorDetails = Array.isArray(item.vendorDetails) ? item.vendorDetails : [];
      const vendorNames = vendorDetails
        .filter((vendor: any) => vendor && typeof vendor === 'object')
        .map((vendor: any) => (vendor?.vendorCorporateName || '').trim())
        .filter(Boolean);
      
      const vendorStatuses = vendorDetails
        .filter((vendor: any) => vendor && typeof vendor === 'object')
        .map((vendor: any) => vendor?.vendorAssessmentStatus)
        .filter(Boolean);

      // Determine primary status based on user role
      // For vendor users: prioritize vendorAssessmentStatus
      // For TJ users: use businessStatus
      const fallbackStatus = vendorStatuses[0] || 'Unknown';
      // Use normalized `vendorDetails` (never null) — `item?.vendorDetails[0]` throws when vendorDetails is null.
      const primaryStatus = isVendor && vendorDetails.length > 0
        ? vendorDetails[0]?.vendorAssessmentStatus || fallbackStatus
        : item?.businessStatus || fallbackStatus;

      // Transform the data with comprehensive null checks
      const transformed: TransformedActiveProject = {
        id: String(item.id || `unknown-${index}`),
        title: (item?.title || item?.name || '').trim(),
        description: item.description || '',
        addedOn: item.addedOn || item.createdAt || new Date().toISOString(),
        productSpecificationType: item.productSpecificationType || item.category || 'Unknown',
        sku: item.sku || '',
        productType: item.productType || '',
        giftPack: String(item.giftPack || 'false'),
        amountOfProductsInGiftPack: String(item.amountOfProductsInGiftPack || '1'),
        retailPrice: Number(item.retailPrice || 0),
        businessCategory: item.businessCategory && typeof item.businessCategory === 'object' ? {
          id: String(item.businessCategory.id || ''),
          categoryId: String(item.businessCategory.categoryId || ''),
          description: String(item.businessCategory.description || ''),
          categoryType: String(item.businessCategory.categoryType || ''),
          leader: String(item.businessCategory.leader || ''),
          manager: item.businessCategory.manager || null
        } : {
          id: '',
          categoryId: '',
          description: '',
          categoryType: '',
          leader: '',
          manager: null
        },
        categoryManagerUser: item.categoryManagerUser && typeof item.categoryManagerUser === 'object' ? {
          id: String(item.categoryManagerUser.id || ''),
          firstname: String(item.categoryManagerUser.firstname || ''),
          username: String(item.categoryManagerUser.username || ''),
          email: String(item.categoryManagerUser.email || '')
        } : {
          id: '',
          firstname: '',
          username: '',
          email: ''
        },
        qa: item.qa && typeof item.qa === 'object' ? {
          id: String(item.qa.id || ''),
          firstname: String(item.qa.firstname || ''),
          username: String(item.qa.username || ''),
          email: String(item.qa.email || '')
        } : {
          id: '',
          firstname: '',
          username: '',
          email: ''
        },
        productCreator: item.productCreator && typeof item.productCreator === 'object' ? {
          id: String(item.productCreator.id || ''),
          firstname: String(item.productCreator.firstname || ''),
          username: String(item.productCreator.username || ''),
          email: String(item.productCreator.email || '')
        } : {
          id: '',
          firstname: '',
          username: '',
          email: ''
        },
        nutrition: item.nutrition && typeof item.nutrition === 'object' ? {
          id: String(item.nutrition.id || ''),
          firstname: String(item.nutrition.firstname || ''),
          username: String(item.nutrition.username || ''),
          email: String(item.nutrition.email || '')
        } : {
          id: '',
          firstname: '',
          username: '',
          email: ''
        },
        buyer: item.buyer && typeof item.buyer === 'object' ? {
          id: String(item.buyer.id || ''),
          firstname: String(item.buyer.firstname || ''),
          username: String(item.buyer.username || ''),
          email: String(item.buyer.email || '')
        } : {
          id: '',
          firstname: '',
          username: '',
          email: ''
        },
        variants: Array.isArray(item.variants) ? item.variants : [],
        vendorAssessments: vendorDetails.map((vendor: any) => ({
          id: String(vendor.id || ''),
          status: String(vendor.vendorAssessmentStatus || ''),
          vendor: {
            id: String(vendor.id || ''),
            vendorCorporateName: String(vendor.vendorCorporateName || '').trim()
          },
          vendorManufacturingFacilities: Array.isArray(vendor.vendorManufacturingFacilities) 
            ? vendor.vendorManufacturingFacilities 
            : []
        })),
        // Also create vendorDetails for MultiVendorTooltip compatibility
        vendorDetails: vendorDetails.map((vendor: any) => ({
          id: String(vendor.id || ''),
          vendorId: String(vendor.vendorId || ''),
          vendorName: String(vendor.vendorCorporateName || '').trim(),
          vendorAssessmentStatus: String(vendor.vendorAssessmentStatus || ''),
          published: vendor.ingredientStatus?.toLowerCase() === 'ingredient approved' ? true :
                   vendor.ingredientStatus?.toLowerCase() === 'ingredient concern' ? false : null,
          vendorIngredientDetails: vendor.vendorIngredientDetails || [],
          artworkDeadline: vendor.artworkDeadline || '',
          workflowStatus: Array.isArray(vendor.workflowStatus) ? vendor.workflowStatus : []
        })),
        // Additional fields for datatable compatibility
        businessStatus:
          item?.businessStatus != null && String(item.businessStatus).trim() !== ""
            ? String(item.businessStatus).trim()
            : "",
        status: primaryStatus,
        vendor: vendorNames.length === 1 ? vendorNames[0] : vendorNames,
        category: item.productSpecificationType || item.category || 'Unknown'
      };

      return transformed;
    } catch (error) {
      console.log('active project data transformer error', error);
      return createFallbackItem(index);
    }
  });
}

/**
 * Extract filter values from transformed data
 * @param transformedData - Transformed active projects data
 * @returns Filter values object
 */
export function extractFilterValues(transformedData: TransformedActiveProject[]) {
  // Null check for input data
  if (!Array.isArray(transformedData)) {
    return {
      Status: [],
      Product_Types: [],
      Categories: [],
      Category_Manager: [],
      Product_Creator: [],
      Vendors: []
    };
  }

  const uniqueValues = {
    Status: Array.from(new Set(transformedData
      .filter(item => item && typeof item === 'object')
      .map(item => item.status)
      .filter(Boolean)
    )).map((value, index) => ({
      id: String(index + 1),
      label: value || 'Unknown',
      value: (value || 'unknown').toLowerCase().replace(/\s+/g, '_'),
      checked: false,
      disabled: false
    })),
    Product_Types: Array.from(new Set(transformedData
      .filter(item => item && typeof item === 'object')
      .map(item => item.productSpecificationType)
      .filter(Boolean)
    )).map((value, index) => ({
      id: String(index + 1),
      label: value || 'Unknown',
      value: (value || 'unknown').toLowerCase().replace(/\s+/g, '_'),
      checked: false,
      disabled: false
    })),
    Categories: Array.from(new Set(transformedData
      .filter(item => item && typeof item === 'object')
      .map(item => item.productSpecificationType)
      .filter(Boolean)
    )).map((value, index) => ({
      id: String(index + 1),
      label: value || 'Unknown',
      value: (value || 'unknown').toLowerCase().replace(/\s+/g, '_'),
      checked: false,
      disabled: false
    })),
    Category_Manager: Array.from(new Set(transformedData
      .filter(item => item && typeof item === 'object' && item.categoryManagerUser)
      .map(item => item.categoryManagerUser?.firstname)
      .filter(Boolean)
    )).map((value, index) => ({
      id: String(index + 1),
      label: value || 'Unknown',
      value: (value || 'unknown').toLowerCase().replace(/\s+/g, '_'),
      checked: false,
      disabled: false
    })),
    Product_Creator: Array.from(new Set(transformedData
      .filter(item => item && typeof item === 'object' && item.productCreator)
      .map(item => item.productCreator?.firstname)
      .filter(Boolean)
    )).map((value, index) => ({
      id: String(index + 1),
      label: value || 'Unknown',
      value: (value || 'unknown').toLowerCase().replace(/\s+/g, '_'),
      checked: false,
      disabled: false
    })),
    Vendors: Array.from(new Set(transformedData
      .filter(item => item && typeof item === 'object')
      .flatMap(item => {
        if (Array.isArray(item.vendor)) {
          return item.vendor.filter(Boolean);
        }
        return item.vendor ? [item.vendor] : [];
      })
      .filter(Boolean)
    )).map((value, index) => ({
      id: String(index + 1),
      label: value || 'Unknown',
      value: (value || 'unknown').toLowerCase().replace(/\s+/g, '_'),
      checked: false,
      disabled: false
    }))
  };

  return uniqueValues;
}

/**
 * Debug function to log data transformation details
 * @param rawData - Raw API data
 * @param transformedData - Transformed data
 */
